import { useCallback, useEffect, useRef, useState } from 'react'
import { getLatestNews, type NewsEdition } from '../services/news/client'
import { isNewerNewsEdition, newestNewsData } from '../services/news/edition'
import type { NewsResponse } from '../types/news'

const NEWS_POLL_INTERVAL_MS = 60 * 60 * 1000
const NEWS_REQUEST_TIMEOUT_MS = 8_000
const NEWS_STORAGE_PREFIX = 'omikina:news-cache:v1:'

function isNewsResponse(payload: unknown, edition: NewsEdition): payload is NewsResponse {
  if (!payload || typeof payload !== 'object') return false
  const candidate = payload as Partial<NewsResponse>
  return Array.isArray(candidate.items)
    && candidate.items.length > 0
    && Array.isArray(candidate.sources)
    && typeof candidate.refreshedAt === 'string'
    && typeof candidate.nextRefreshAt === 'string'
    && (!candidate.edition || candidate.edition === edition)
}

function readEmbeddedNewsData(edition: NewsEdition): NewsResponse | null {
  const node = document.getElementById(`omikina-news-data-${edition}`)
  if (!node?.textContent) return null
  try {
    const payload = JSON.parse(node.textContent) as unknown
    return isNewsResponse(payload, edition) ? payload : null
  } catch {
    return null
  }
}

function readStoredNewsData(edition: NewsEdition): NewsResponse | null {
  try {
    const payload = JSON.parse(window.localStorage.getItem(`${NEWS_STORAGE_PREFIX}${edition}`) ?? '') as unknown
    return isNewsResponse(payload, edition) ? { ...payload, cache: 'stale' } : null
  } catch {
    return null
  }
}

function writeStoredNewsData(edition: NewsEdition, payload: NewsResponse): void {
  try { window.localStorage.setItem(`${NEWS_STORAGE_PREFIX}${edition}`, JSON.stringify(payload)) } catch { /* Browser storage is an optional speed layer. */ }
}

function readInitialNewsData(edition: NewsEdition): NewsResponse | null {
  const embedded = readEmbeddedNewsData(edition)
  const stored = readStoredNewsData(edition)
  return stored ? newestNewsData(embedded, stored) : embedded
}

export interface NewsFeedState {
  data: NewsResponse | null
  loading: boolean
  error: string | null
  pendingData: NewsResponse | null
  hasNewEdition: boolean
  applyNewEdition: () => void
}

export function useNewsFeed(enabled = true, edition: NewsEdition = 'ai'): NewsFeedState {
  const [data, setData] = useState<NewsResponse | null>(() => readInitialNewsData(edition))
  const [pendingData, setPendingData] = useState<NewsResponse | null>(null)
  const [loading, setLoading] = useState(enabled && data === null)
  const [error, setError] = useState<string | null>(null)
  const hasImmediateData = useRef(data !== null)
  const latestData = useRef(data)
  const visibleData = useRef(data)
  const pendingDataRef = useRef<NewsResponse | null>(null)

  const applyNewEdition = useCallback(() => {
    const next = pendingDataRef.current
    if (!next) return
    visibleData.current = next
    latestData.current = next
    pendingDataRef.current = null
    setData(next)
    setPendingData(null)
  }, [])

  useEffect(() => {
    if (!enabled) return
    let disposed = false
    let activeController: AbortController | null = null
    let lastRequestAt = 0
    let hasFetchedEdition = false
    const load = (force = false) => {
      const now = Date.now()
      if (!force && now - lastRequestAt < NEWS_POLL_INTERVAL_MS) return
      lastRequestAt = now
      activeController?.abort()
      const controller = new AbortController()
      activeController = controller
      const timeoutId = window.setTimeout(() => controller.abort(), NEWS_REQUEST_TIMEOUT_MS)
      if (!hasImmediateData.current) setLoading(true)
      getLatestNews(controller.signal, edition)
        .then((response) => {
          if (disposed) return
          const next = newestNewsData(latestData.current, response)
          latestData.current = next
          hasImmediateData.current = true
          writeStoredNewsData(edition, next)
          // A new visit catches up automatically; only later polling interrupts
          // an active reading session with the optional new-edition control.
          if (!hasFetchedEdition || !visibleData.current) {
            visibleData.current = next
            pendingDataRef.current = null
            setData(next)
            setPendingData(null)
          } else if (isNewerNewsEdition(visibleData.current, next)) {
            pendingDataRef.current = next
            setPendingData(next)
          }
          hasFetchedEdition = true
          setError(null)
        })
        .catch((cause: unknown) => {
          if (!disposed && !hasImmediateData.current && (cause as Error).name !== 'AbortError') setError('NEWS FEED TEMPORARILY UNAVAILABLE')
        })
        .finally(() => {
          window.clearTimeout(timeoutId)
          if (!disposed) setLoading(false)
        })
    }

    load(true)
    const intervalId = window.setInterval(() => load(true), NEWS_POLL_INTERVAL_MS)
    const handleVisibility = () => { if (document.visibilityState === 'visible') load(false) }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      disposed = true
      activeController?.abort()
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [enabled, edition])

  return { data, loading, error, pendingData, hasNewEdition: pendingData !== null, applyNewEdition }
}
